<template>
  <Breadcrumb v-if="!isLoading" class="bg-surface-default-mid" :models-names />
  <Skeleton v-if="isLoading" class="pt-32" />
  <RevContainer v-else class="pt-32 md:pb-72">
    <h1 class="heading-1 text-center">
      {{ title }}
    </h1>
    <Transition
      enter-active-class="ease-out duration-150"
      enter-from-class="-translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="ease-in duration-150"
      leave-from-class="translate-y-0"
      leave-to-class="-translate-y-full"
    >
      <div
        v-show="isScrolled"
        class="bg-surface-default-low border-static-default-low sticky left-0 right-0 top-0 z-[1] border-b"
      >
        <div
          class="mx-auto grid grid-cols-2 gap-x-24 pb-12 pt-16 lg:gap-x-40 lg:pb-24"
        >
          <ProductCard
            v-for="product in products"
            :key="`sticky-card-${product.productId}`"
            :product
            sticky
          />
        </div>
      </div>
    </Transition>
    <div class="mx-auto mt-24">
      <div ref="bodyContainer" class="grid grid-cols-2 gap-x-24 lg:gap-x-40">
        <ProductCard
          v-for="product in products"
          :key="`card-${product.productId}`"
          :product
        />
      </div>
      <TableContent class="mt-16" :nb-columns="2" :table-data />
    </div>
  </RevContainer>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevContainer } from '@ds/components/Container'
import { useIntersectionObserver } from '@vueuse/core'

import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import { useEnrichResultsPage } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage'
import type { ComparisonData } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage.types'
import { useModelsComparisonHead } from '../../../composables/useModelsComparisonHeadV2/useModelsComparisonHeadV2'
import Breadcrumb from '../../Breadcrumb/Breadcrumb.vue'
import ProductCard from '../ProductCard/ProductCard.vue'
import Skeleton from '../Skeleton/Skeleton.vue'
import TableContent from '../TableContent/TableContent.vue'

import translations from './Main.translations'

const props = defineProps<{
  comparisonData: ComparisonData | null | undefined
  comparisonPending: boolean
}>()

const { products, tableData, pending } = useEnrichResultsPage(
  toRef(props, 'comparisonData'),
  toRef(props, 'comparisonPending'),
)

const i18n = useI18n()
const isScrolled = ref(false)
const bodyContainer = ref()

const title = computed(() =>
  i18n(translations.resultsTitle, {
    firstModel: products.value[0]?.title,
    secondModel: products.value[1]?.title,
  }),
)

const isLoading = computed(() => pending.value || !products.value.length)
const modelsNames = computed(() => products.value.map((p) => p.title))

useIntersectionObserver(
  bodyContainer,
  ([{ boundingClientRect }]) => {
    isScrolled.value = boundingClientRect.top < -100
  },
  { threshold: [0, 1] },
)

const productNames = computed(() =>
  products.value.length >= 2
    ? [products.value[0].title, products.value[1].title]
    : null,
)
useModelsComparisonHead(productNames)

useAddCanonicalLink()
</script>
